import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { MenuItem, DietaryFlag } from '../types';
import { formatEuro } from '../utils/format';
import { ItemDetailModal } from './ItemDetailModal';
import { FoodTypeBadge } from './FoodTypeBadge';
import { HeroSlider } from './HeroSlider';
import {
  Search,
  Sparkles,
  Flame,
  Plus,
  Minus,
  Check,
  Gift,
  Tag,
  ChevronDown,
  ChevronUp,
  Utensils,
  Star,
  X,
  Percent,
  Wine
} from 'lucide-react';

export const MenuSection: React.FC = () => {
  const {
    categories,
    addToCart,
    cart,
    updateCartQuantity,
    removeFromCart,
    heroSettings,
    offerBanners,
    applyCoupon,
    setIsCartDrawerOpen
  } = useApp();
  const { lang, t } = useLanguage();

  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('all');
  const [dietaryFilter, setDietaryFilter] = useState<'ALL' | 'VEG' | 'NON_VEG' | 'VEGAN' | 'G' | 'L' | 'M' | 'ALCOHOL' | 'BESTSELLER'>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeModalItem, setActiveModalItem] = useState<MenuItem | null>(null);
  const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>({});
  const [isMenuJumpSheetOpen, setIsMenuJumpSheetOpen] = useState(false);
  const [offerToast, setOfferToast] = useState<string | null>(null);

  // Localized Hero text
  const heroTitle = lang === 'en' ? heroSettings.title_en : lang === 'sv' ? heroSettings.title_sv : heroSettings.title_fi;
  const heroDesc = lang === 'en' ? heroSettings.desc_en : lang === 'sv' ? heroSettings.desc_sv : heroSettings.desc_fi;

  // Active promotional banners
  const activeOffers = offerBanners.filter(b => b.is_active);

  // Filter items
  const filteredCategories = useMemo(() => {
    return categories
      .filter(cat => cat.is_active && (selectedCategoryId === 'all' || cat.id === selectedCategoryId))
      .map(cat => {
        let items = cat.items || [];

        if (dietaryFilter === 'VEG') {
          items = items.filter(i => i.dietary_flags.includes('VEG') && !i.is_alcohol);
        } else if (dietaryFilter === 'NON_VEG') {
          items = items.filter(i => !i.dietary_flags.includes('VEG') && !i.is_alcohol);
        } else if (dietaryFilter === 'VEGAN') {
          items = items.filter(i => i.dietary_flags.includes('VEG') && (i.dietary_flags.includes('M') || i.name_en.toLowerCase().includes('vegan')));
        } else if (dietaryFilter === 'G') {
          items = items.filter(i => i.dietary_flags.includes('G'));
        } else if (dietaryFilter === 'L') {
          items = items.filter(i => i.dietary_flags.includes('L'));
        } else if (dietaryFilter === 'M') {
          items = items.filter(i => i.dietary_flags.includes('M'));
        } else if (dietaryFilter === 'ALCOHOL') {
          items = items.filter(i => i.is_alcohol);
        } else if (dietaryFilter === 'BESTSELLER') {
          items = items.filter(i => i.is_popular || i.is_recommended);
        }

        if (searchQuery.trim()) {
          const q = searchQuery.toLowerCase().trim();
          items = items.filter(i => {
            const name = (lang === 'en' ? i.name_en : lang === 'sv' ? i.name_sv : i.name_fi).toLowerCase();
            const desc = (lang === 'en' ? i.description_en : lang === 'sv' ? i.description_sv : i.description_fi).toLowerCase();
            return name.includes(q) || desc.includes(q);
          });
        }

        return {
          ...cat,
          filteredItems: items
        };
      })
      .filter(cat => cat.filteredItems.length > 0);
  }, [categories, selectedCategoryId, dietaryFilter, searchQuery, lang]);

  const toggleCategoryCollapse = (catId: string) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [catId]: !prev[catId]
    }));
  };

  const getItemCartItems = (itemId: string) => {
    return cart.filter(c => c.menu_item.id === itemId);
  };

  const getItemCartTotalQty = (itemId: string) => {
    return cart
      .filter(c => c.menu_item.id === itemId)
      .reduce((sum, c) => sum + c.quantity, 0);
  };

  const handleCardClick = (item: MenuItem) => {
    if (!item.is_available) return;
    setActiveModalItem(item);
  };

  const handleAddClick = (e: React.MouseEvent, item: MenuItem) => {
    e.stopPropagation();
    if (item.variants && item.variants.length > 0) {
      setActiveModalItem(item);
    } else {
      addToCart(item, 1, []);
    }
  };

  const handleDecrement = (e: React.MouseEvent, item: MenuItem) => {
    e.stopPropagation();
    const cartItems = getItemCartItems(item.id);
    if (cartItems.length === 1) {
      updateCartQuantity(cartItems[0].cart_item_id, cartItems[0].quantity - 1);
    } else if (cartItems.length > 1) {
      updateCartQuantity(cartItems[cartItems.length - 1].cart_item_id, cartItems[cartItems.length - 1].quantity - 1);
    }
  };

  const handleIncrement = (e: React.MouseEvent, item: MenuItem) => {
    e.stopPropagation();
    if (item.variants && item.variants.length > 0) {
      setActiveModalItem(item);
    } else {
      const cartItems = getItemCartItems(item.id);
      if (cartItems.length > 0) {
        updateCartQuantity(cartItems[0].cart_item_id, cartItems[0].quantity + 1);
      } else {
        addToCart(item, 1, []);
      }
    }
  };

  const scrollToCategory = (catId: string) => {
    setIsMenuJumpSheetOpen(false);
    setSelectedCategoryId('all');
    setTimeout(() => {
      const el = document.getElementById(`cat_section_${catId}`);
      if (el) {
        const yOffset = -140;
        const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 100);
  };

  const handleOfferClick = (code?: string) => {
    if (code) {
      applyCoupon(code);
      setIsCartDrawerOpen(true);
    }
  };

  return (
    <div className="menu-container">
      {/* 1. Dynamic Hero & Promotional Carousel Slider */}
      <HeroSlider />

      {/* 2. Sleek Wolt-Style Promotional Offer Strip */}
      {activeOffers.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {activeOffers.map(offer => {
            const offerTitle = lang === 'en' ? offer.title_en : lang === 'sv' ? offer.title_sv : offer.title_fi;

            return (
              <div
                key={offer.id}
                onClick={() => handleOfferClick(offer.discount_code)}
                className="offer-strip"
              >
                <div className="offer-strip-left">
                  <div className="offer-strip-badge">
                    <span>{offer.badge || 'DEAL'}</span>
                  </div>
                  <div className="offer-strip-title">{offerTitle}</div>
                </div>

                {offer.discount_code && (
                  <button
                    type="button"
                    className="offer-strip-code-btn"
                    title="Click to apply discount to your cart"
                  >
                    <Tag size={12} />
                    <span>{offer.discount_code}</span>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* 3. Search & Filter Bar */}
      <div className="search-filter-container">
        {/* Search Bar */}
        <div className="search-input-wrapper">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder={t('searchPlaceholder')}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Categories Ribbon */}
        <div className="category-nav">
          <button
            className={`category-pill ${selectedCategoryId === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedCategoryId('all')}
          >
            <span>🍽️</span>
            <span>{t('allCategories')}</span>
          </button>
          {categories.map(cat => {
            const catName = lang === 'en' ? cat.name_en : lang === 'sv' ? cat.name_sv : cat.name_fi;
            return (
              <button
                key={cat.id}
                className={`category-pill ${selectedCategoryId === cat.id ? 'active' : ''}`}
                onClick={() => setSelectedCategoryId(cat.id)}
              >
                <span>{cat.icon || '🍕'}</span>
                <span>{catName}</span>
              </button>
            );
          })}
        </div>

        {/* Dietary & Food Type Filters (Veg, Non-Veg, Vegan, Gluten-Free, Lactose-Free, Dairy-Free, Alcohol) */}
        <div className="dietary-tags-row">
          <button
            className={`dietary-filter-pill ${dietaryFilter === 'ALL' ? 'active' : ''}`}
            onClick={() => setDietaryFilter('ALL')}
          >
            {t('dietaryAll')}
          </button>

          {/* 1. Veg */}
          <button
            className={`dietary-filter-pill ${dietaryFilter === 'VEG' ? 'active' : ''}`}
            onClick={() => setDietaryFilter(dietaryFilter === 'VEG' ? 'ALL' : 'VEG')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <FoodTypeBadge type="veg" size={15} showTooltip={false} />
            <span>Veg</span>
          </button>

          {/* 2. Non-Veg */}
          <button
            className={`dietary-filter-pill ${dietaryFilter === 'NON_VEG' ? 'active' : ''}`}
            onClick={() => setDietaryFilter(dietaryFilter === 'NON_VEG' ? 'ALL' : 'NON_VEG')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <FoodTypeBadge type="non-veg" size={15} showTooltip={false} />
            <span>Non-Veg</span>
          </button>

          {/* 3. Vegan (Leaf Icon) */}
          <button
            className={`dietary-filter-pill ${dietaryFilter === 'VEGAN' ? 'active' : ''}`}
            onClick={() => setDietaryFilter(dietaryFilter === 'VEGAN' ? 'ALL' : 'VEGAN')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <FoodTypeBadge type="vegan" size={15} showTooltip={false} />
            <span>Vegan</span>
          </button>

          {/* 4. Gluten-Free (G) */}
          <button
            className={`dietary-filter-pill ${dietaryFilter === 'G' ? 'active' : ''}`}
            onClick={() => setDietaryFilter(dietaryFilter === 'G' ? 'ALL' : 'G')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <FoodTypeBadge type="gluten-free" size={15} showTooltip={false} />
            <span>Gluten-Free</span>
          </button>

          {/* 5. Lactose-Free (L) */}
          <button
            className={`dietary-filter-pill ${dietaryFilter === 'L' ? 'active' : ''}`}
            onClick={() => setDietaryFilter(dietaryFilter === 'L' ? 'ALL' : 'L')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <FoodTypeBadge type="lactose-free" size={15} showTooltip={false} />
            <span>Lactose-Free</span>
          </button>

          {/* 6. Dairy-Free (M) */}
          <button
            className={`dietary-filter-pill ${dietaryFilter === 'M' ? 'active' : ''}`}
            onClick={() => setDietaryFilter(dietaryFilter === 'M' ? 'ALL' : 'M')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <FoodTypeBadge type="dairy-free" size={15} showTooltip={false} />
            <span>Dairy-Free (M)</span>
          </button>

          {/* 7. Alcohol (18+) */}
          <button
            className={`dietary-filter-pill ${dietaryFilter === 'ALCOHOL' ? 'active' : ''}`}
            onClick={() => setDietaryFilter(dietaryFilter === 'ALCOHOL' ? 'ALL' : 'ALCOHOL')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}
          >
            <Wine size={13} color="#f43f5e" />
            <span>Alcohol (18+)</span>
          </button>

          {/* 8. Bestseller */}
          <button
            className={`dietary-filter-pill ${dietaryFilter === 'BESTSELLER' ? 'active' : ''}`}
            onClick={() => setDietaryFilter(dietaryFilter === 'BESTSELLER' ? 'ALL' : 'BESTSELLER')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
          >
            <Star size={12} color="#f59e0b" fill={dietaryFilter === 'BESTSELLER' ? '#f59e0b' : 'none'} />
            <span>Bestseller</span>
          </button>
        </div>
      </div>

      {/* 4. Swiggy-Style Category Sections */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {filteredCategories.map(cat => {
          const catName = lang === 'en' ? cat.name_en : lang === 'sv' ? cat.name_sv : cat.name_fi;
          const isCollapsed = !!collapsedCategories[cat.id];

          return (
            <div
              key={cat.id}
              id={`cat_section_${cat.id}`}
              className="swiggy-category-section"
            >
              {/* Category Header */}
              <div
                className="swiggy-category-header"
                onClick={() => toggleCategoryCollapse(cat.id)}
              >
                <div className="swiggy-category-title-group">
                  <span style={{ fontSize: '1.15rem' }}>{cat.icon || '🍕'}</span>
                  <div className="swiggy-category-title">{catName}</div>
                  <span className="swiggy-category-count">
                    {cat.filteredItems.length}
                  </span>
                </div>

                <div style={{ color: 'var(--text-muted)' }}>
                  {isCollapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
                </div>
              </div>

              {/* Items List */}
              {!isCollapsed && (
                <div className="swiggy-items-list">
                  {cat.filteredItems.map(item => {
                    const itemName = lang === 'en' ? item.name_en : lang === 'sv' ? item.name_sv : item.name_fi;
                    const itemDesc = lang === 'en' ? item.description_en : lang === 'sv' ? item.description_sv : item.description_fi;
                    const isVegan = item.dietary_flags.includes('VEG');
                    const isGlutenFree = item.dietary_flags.includes('G');
                    const isLactoseFree = item.dietary_flags.includes('L');
                    const isDairyFree = item.dietary_flags.includes('M');
                    const isBestseller = item.is_popular || item.is_recommended;
                    const inCartQty = getItemCartTotalQty(item.id);
                    const hasVariants = item.variants && item.variants.length > 0;

                    return (
                      <div
                        key={item.id}
                        className={`swiggy-dish-card ${!item.is_available ? 'unavailable' : ''}`}
                        onClick={() => handleCardClick(item)}
                      >
                        {/* Left Info */}
                        <div className="swiggy-dish-left">
                          <div className="swiggy-dish-tags-row">
                            {/* 1. Food Type Icons (Veg, Vegan, Non-Veg) */}
                            {!item.is_alcohol && (
                              isVegan ? (
                                <FoodTypeBadge type="vegan" size={16} />
                              ) : (
                                <FoodTypeBadge type={item.dietary_flags.includes('L') && !item.name_en.toLowerCase().includes('reindeer') && !item.name_en.toLowerCase().includes('nduja') && !item.name_en.toLowerCase().includes('salmon') ? 'veg' : 'non-veg'} size={16} />
                              )
                            )}

                            {/* 2. Gluten-Free (G) Icon */}
                            {isGlutenFree && (
                              <FoodTypeBadge type="gluten-free" size={16} />
                            )}

                            {/* 3. Lactose-Free (L) Icon */}
                            {isLactoseFree && (
                              <FoodTypeBadge type="lactose-free" size={16} />
                            )}

                            {/* 4. Dairy-Free (M) Icon */}
                            {isDairyFree && !isVegan && (
                              <FoodTypeBadge type="dairy-free" size={16} />
                            )}

                            {/* 5. Alcohol Badge */}
                            {item.is_alcohol && (
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '3px',
                                  fontSize: '0.68rem',
                                  fontWeight: 800,
                                  color: '#f43f5e',
                                  background: 'rgba(244,63,94,0.1)',
                                  padding: '1px 6px',
                                  borderRadius: '3px',
                                  border: '1px solid rgba(244,63,94,0.3)'
                                }}
                              >
                                <Wine size={11} />
                                <span>18+ {item.alcohol_abv ? `• ${item.alcohol_abv}%` : ''}</span>
                              </span>
                            )}

                            {/* 6. Bestseller Badge */}
                            {isBestseller && (
                              <span className="swiggy-bestseller-badge">
                                <Star size={10} fill="#d97706" color="#d97706" />
                                <span>BESTSELLER</span>
                              </span>
                            )}
                          </div>

                          <div className="swiggy-dish-name">{itemName}</div>
                          <div className="swiggy-dish-price">{formatEuro(item.price)}</div>
                          <div className="swiggy-dish-desc">{itemDesc}</div>
                        </div>

                        {/* Right Photo & Stepper */}
                        <div className="swiggy-dish-right">
                          <img
                            src={item.image_url}
                            alt={itemName}
                            className="swiggy-dish-img"
                            loading="lazy"
                          />

                          <div className="swiggy-add-container">
                            {item.is_available ? (
                              <>
                                {inCartQty === 0 ? (
                                  <button
                                    className="swiggy-add-btn"
                                    onClick={e => handleAddClick(e, item)}
                                  >
                                    ADD <span style={{ fontSize: '0.9rem', marginLeft: '2px' }}>+</span>
                                  </button>
                                ) : (
                                  <div className="swiggy-qty-stepper">
                                    <div
                                      className="swiggy-stepper-btn"
                                      onClick={e => handleDecrement(e, item)}
                                    >
                                      <Minus size={13} />
                                    </div>
                                    <span>{inCartQty}</span>
                                    <div
                                      className="swiggy-stepper-btn"
                                      onClick={e => handleIncrement(e, item)}
                                    >
                                      <Plus size={13} />
                                    </div>
                                  </div>
                                )}

                                {hasVariants && (
                                  <span className="swiggy-customisable-hint">Customisable</span>
                                )}
                              </>
                            ) : (
                              <div
                                style={{
                                  background: 'rgba(0,0,0,0.75)',
                                  color: '#fff',
                                  padding: '3px 6px',
                                  borderRadius: 'var(--radius-xs)',
                                  fontSize: '0.68rem',
                                  fontWeight: 700
                                }}
                              >
                                {t('soldOut')}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* 5. Signature Floating "MENU" Jump Pill (Swiggy Style) */}
      <button
        className={`swiggy-floating-menu-pill no-print ${cart.length > 0 ? 'has-cart' : ''}`}
        onClick={() => setIsMenuJumpSheetOpen(true)}
      >
        <Utensils size={15} color="var(--accent-copper)" />
        <span>MENU</span>
      </button>

      {/* Quick Jump Category Bottom Sheet */}
      {isMenuJumpSheetOpen && (
        <div className="modal-overlay" onClick={() => setIsMenuJumpSheetOpen(false)}>
          <div
            className="modal-card"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '380px', borderRadius: 'var(--radius-xl)', maxHeight: '75vh' }}
          >
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Utensils size={18} color="var(--accent-copper)" />
                <div className="modal-title">Browse Menu Categories</div>
              </div>
              <button className="modal-close-btn" onClick={() => setIsMenuJumpSheetOpen(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '16px' }}>
              {categories.map(cat => {
                const catName = lang === 'en' ? cat.name_en : lang === 'sv' ? cat.name_sv : cat.name_fi;
                const itemCount = cat.items?.length || 0;

                return (
                  <button
                    key={cat.id}
                    onClick={() => scrollToCategory(cat.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      background: 'var(--bg-surface)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--border-subtle)',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      color: 'var(--text-primary)',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span>{cat.icon || '🍕'}</span>
                      <span>{catName}</span>
                    </div>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                      {itemCount}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Dish Customization Modal */}
      {activeModalItem && (
        <ItemDetailModal
          item={activeModalItem}
          onClose={() => setActiveModalItem(null)}
        />
      )}
    </div>
  );
};

export default MenuSection;
